import { Bell } from 'lucide-react';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useProductUpdates } from '@/hooks/queries/useProductUpdatesQuery';

// Full changelog lives in the bundled client docs (docs_dir=src/client, so it
// builds to changelog.html at the bundle root), opened as a static file.
const FULL_CHANGELOG_HREF = '/documentation/changelog.html';

/** Header bell that surfaces the weekly client changelog with an unread dot. */
export function WhatsNewBell() {
    const [open, setOpen] = useState(false);
    const { updates, unreadCount, markSeen } = useProductUpdates();

    if (updates.length === 0) return null;

    const onOpenChange = (next: boolean) => {
        setOpen(next);
        if (next) markSeen(); // opening the panel clears the unread badge
    };

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label={
                        unreadCount > 0 ? `What's new (${unreadCount} unread)` : "What's new"
                    }
                    title="What's new"
                    className="relative flex h-7 w-7 items-center justify-center rounded-md text-foreground-subtle transition-colors hover:bg-sidebar-accent hover:text-foreground"
                >
                    <Bell className="h-4 w-4" />
                    {unreadCount > 0 && (
                        <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-[var(--accent-foreground)] ring-2 ring-background" />
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="max-h-[70vh] w-96 overflow-y-auto p-0">
                <div className="border-border border-b px-4 py-3">
                    <h2 className="font-semibold text-foreground text-sm">What's new</h2>
                </div>
                <div className="divide-y divide-border">
                    {updates.map((u) => (
                        <article key={u.id} className="px-4 py-3">
                            <h3 className="mb-1 font-medium text-foreground text-sm">{u.title}</h3>
                            <div className="prose-sm text-muted-foreground text-sm [&_a]:text-[var(--link)] [&_a]:underline [&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:font-medium [&_h3]:text-foreground [&_h3]:text-xs [&_h3]:uppercase [&_h3]:tracking-wide [&_li]:my-1 [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-4">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {u.body_md}
                                </ReactMarkdown>
                            </div>
                        </article>
                    ))}
                </div>
                <div className="border-border border-t px-4 py-2 text-center">
                    <a
                        href={FULL_CHANGELOG_HREF}
                        onClick={() => setOpen(false)}
                        className="text-[var(--link)] text-xs hover:underline"
                    >
                        View all updates →
                    </a>
                </div>
            </PopoverContent>
        </Popover>
    );
}
